import type { AccountBalance } from "@ekidenfi/ts-sdk";
import { makeAutoObservable, reaction, runInAction } from "mobx";

type AccountBalanceResponse = AccountBalance & { sub_account_address?: string };

import { TOKEN_METADATA } from "@constants/metadata";
import { Address, isValidAddress } from "@utils/Address";
import { BN } from "@utils/BN";
import { formatAmount, formatUsd } from "@utils/Format";
import { IntervalUpdater } from "@utils/IntervalUpdater";

import { balanceService } from "@src/services";

import type { RootStore } from "./RootStore";

export type TokenBalance = {
  address: string;
  amount: string;
};

export type Token = {
  name: string;
  symbol: string;
  icon: string;
  decimals: number;
  amount: BN;
  amountFormatted: string;
  amountUsd: string;
  address: string;
};

export type TokenWithoutAmount = Omit<Token, "amount" | "amountFormatted" | "amountUsd">;

export class BalanceStore {
  private onChainBalances = new Map<string, Map<string, string>>();
  private accountBalanceByUser = new Map<string, AccountBalance>();
  private fetchBalancePromise: Promise<void> | null = null;
  private onChainBalanceUpdater: IntervalUpdater;

  loading = false;
  isTestTokensRequested = false;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this, {
      getAccountBalanceForUser: false,
    });
    this.onChainBalanceUpdater = new IntervalUpdater(async () => {
      const address = this.rootStore.accountStore.wallet?.account?.address.toString();
      if (isValidAddress(address)) {
        this.fetchOnChainBalance(address);
      }
    }, 30000);
    this.setupReactions();
    this.onChainBalanceUpdater.run();
  }

  get externalAccountBalances(): Token[] {
    const address = this.rootStore.accountStore.wallet?.account?.address.toString();
    if (!address) return [];
    const balance = this.onChainBalances.get(address);
    return this.mapTokenAmounts(balance);
  }

  get vaultBalances(): Token[] {
    const { accountStore, configStore, marketStore } = this.rootStore;

    let totalAvailable = 0;

    if (accountStore.fundingSubAccount) {
      const fundingAddr = Address.from(accountStore.fundingSubAccount.address).toString();
      const fundingBalance = this.accountBalanceByUser.get(fundingAddr);
      const available = Number(fundingBalance?.available_balance || 0);
      totalAvailable += available;
    }

    for (const tradingAccount of accountStore.tradingSubAccounts) {
      const tradingAddr = Address.from(tradingAccount.address).toString();
      const tradingBalance = this.accountBalanceByUser.get(tradingAddr);
      const available = Number(tradingBalance?.available_balance || 0);
      totalAvailable += available;
    }

    const quoteAssetAddress = configStore.quoteAssetMetadata;
    if (!quoteAssetAddress) {
      return [];
    }

    const quoteAssetNormalized = Address.from(quoteAssetAddress).toString();
    const tokenMetadata = TOKEN_METADATA[quoteAssetAddress] || TOKEN_METADATA[quoteAssetNormalized];

    if (!tokenMetadata) {
      return [];
    }

    const market = marketStore.allMarkets.find((m) => Address.from(m.quote_addr).toString() === quoteAssetNormalized);
    const decimals = market?.quote_decimals ?? marketStore.quoteToken?.decimals ?? 6;

    // Backend returns balance in decimal format, convert to smallest units for consistency
    const rawAmount = new BN(totalAvailable);
    const amount = BN.parseUnits(rawAmount, decimals);

    return [
      {
        name: tokenMetadata.name,
        symbol: tokenMetadata.symbol,
        icon: tokenMetadata.icon,
        decimals,
        address: quoteAssetAddress,
        amount,
        amountFormatted: rawAmount.toFixed(2),
        amountUsd: rawAmount.toFixed(2),
      },
    ];
  }

  getAccountBalanceForUser = (userAddress: string): AccountBalance | null => {
    const addr = Address.from(userAddress).toString();
    const rawBalance = this.accountBalanceByUser.get(addr) || null;
    if (!rawBalance) return null;

    const activeTradingAddress = this.rootStore.accountStore.activeTradingAccount?.address;
    if (!activeTradingAddress) return rawBalance;

    const normalizedActiveAddress = Address.from(activeTradingAddress).toString();
    if (normalizedActiveAddress !== addr) return rawBalance;

    return this.getLiveBalanceForActiveAccount(rawBalance, addr);
  };

  get activeTradingAccountBalance(): AccountBalance | null {
    const activeTradingAddress = this.rootStore.accountStore.activeTradingAccount?.address;
    if (!activeTradingAddress) return null;
    return this.getAccountBalanceForUser(activeTradingAddress);
  }

  getExternalBalanceByAddress = (address: string): Token | undefined => {
    const normalizedAddress = Address.from(address).toString();
    return this.externalAccountBalances.find((token) => Address.from(token.address).toString() === normalizedAddress);
  };

  getVaultBalanceByAddress = (address: string): Token => {
    const token = this.vaultBalances.find((token) => token.address === address);
    if (token) return token;

    return {
      name: "",
      symbol: "",
      icon: "",
      decimals: 0,
      address,
      amount: BN.ZERO,
      amountFormatted: "0",
      amountUsd: "0",
    } satisfies Token;
  };

  getFundingVaultBalanceByAddress = (address: string): Token => {
    return this.buildSubAccountBalance(address, this.rootStore.accountStore.fundingSubAccount);
  };

  getTradingVaultBalanceByAddress = (address: string): Token => {
    return this.buildSubAccountBalance(address, this.rootStore.accountStore.activeTradingAccount);
  };

  private buildSubAccountBalance = (address: string, subAccount: { address: string } | null): Token => {
    const { marketStore } = this.rootStore;

    const assetAddr = Address.from(address).toString();
    const tokenMetadata = TOKEN_METADATA[address] || TOKEN_METADATA[assetAddr];
    const market = marketStore.allMarkets.find((m) => Address.from(m.quote_addr).toString() === assetAddr);
    const decimals = market?.quote_decimals ?? marketStore.quoteToken?.decimals ?? 6;

    if (!subAccount) {
      return {
        name: tokenMetadata?.name ?? "",
        symbol: tokenMetadata?.symbol ?? "",
        icon: tokenMetadata?.icon ?? "",
        decimals,
        address,
        amount: BN.ZERO,
        amountFormatted: "0",
        amountUsd: "0",
      } satisfies Token;
    }

    const subAccountAddr = Address.from(subAccount.address).toString();
    const balance = this.accountBalanceByUser.get(subAccountAddr);

    // Backend returns balance in decimal format (e.g., "0.5"), convert to smallest units for consistency
    const rawAmount = new BN(balance?.available_balance ?? 0);
    const amount = BN.parseUnits(rawAmount, decimals);

    return {
      name: tokenMetadata?.name ?? "",
      symbol: tokenMetadata?.symbol ?? "",
      icon: tokenMetadata?.icon ?? "",
      decimals,
      address,
      amount,
      amountFormatted: rawAmount.toFixed(2),
      amountUsd: rawAmount.toFixed(2),
    };
  };

  getVaultBalanceByAddressWithLeverage = (address: string): Token => {
    const { marketStore } = this.rootStore;

    const token = this.getVaultBalanceByAddress(address);
    if (token.amount.isZero()) return token;

    const amountWithLeverage = token.amount.multipliedBy(marketStore.currentLeverage ?? 1);

    return {
      ...token,
      amount: amountWithLeverage,
      amountFormatted: formatAmount(amountWithLeverage, token.decimals),
      amountUsd: formatAmount(amountWithLeverage, token.decimals),
    } satisfies Token;
  };

  getUSDCVaultBalanceForUser = (userAddress: string): BN => {
    const userAddr = Address.from(userAddress).toString();
    const balance = this.accountBalanceByUser.get(userAddr);

    if (!balance) {
      return BN.ZERO;
    }

    return new BN(balance.available_balance || 0);
  };

  getFormattedUSDCVaultBalanceForUser = (userAddress: string): string => {
    const balance = this.getUSDCVaultBalanceForUser(userAddress);
    return formatUsd(balance);
  };

  setOnChainBalance = (account: string, balances: Map<string, string>) => {
    this.onChainBalances.set(Address.from(account).toString(), balances);
  };

  setAccountBalance = (userAddr: string, balance: AccountBalance) => {
    this.accountBalanceByUser.set(Address.from(userAddr).toString(), balance);
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setTestTokensRequested = (requested: boolean) => {
    this.isTestTokensRequested = requested;
  };

  fetchOnChainBalance = async (account: string) => {
    this.setLoading(true);

    try {
      const quoteAssetMetadata = this.rootStore.configStore.quoteAssetMetadata;
      const result = await balanceService.fetchOnChainBalance(account, quoteAssetMetadata);
      runInAction(() => {
        this.setOnChainBalance(result.account, result.balances);
        this.setLoading(false);
      });
    } catch (_error) {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  fetchAccountBalances = async () => {
    if (this.fetchBalancePromise) {
      return this.fetchBalancePromise;
    }

    let resolvePromise: () => void;
    const placeholderPromise = new Promise<void>((resolve) => {
      resolvePromise = resolve;
    });
    this.fetchBalancePromise = placeholderPromise;

    const { accountStore } = this.rootStore;

    try {
      if (!accountStore.isFullyConnected || !accountStore.rootAccountToken) return;

      const result = await balanceService.fetchAccountBalance();

      runInAction(() => {
        for (const balance of result.balances as AccountBalanceResponse[]) {
          const addr = balance.sub_account_address;
          if (addr) {
            this.setAccountBalance(addr, balance);
          }
        }
      });
    } catch {
    } finally {
      this.fetchBalancePromise = null;
      resolvePromise!();
    }
  };

  applyAccountBalanceUpdate = (update: {
    sub_account_address: string;
    equity: number;
    available_balance: number;
    total_margin_used: number;
  }) => {
    const subAccountAddr = Address.from(update.sub_account_address).toString();
    const existing = this.accountBalanceByUser.get(subAccountAddr) || null;

    const next: AccountBalance = {
      sub_account_address: subAccountAddr,
      account_type: existing?.account_type ?? "",
      equity: update.equity.toString(),
      vault_balance: existing?.vault_balance ?? "0",
      margin_balance: update.equity.toString(),
      available_balance: update.available_balance.toString(),
      accrued_interest: existing?.accrued_interest ?? "0",
      total_order_im: existing?.total_order_im ?? "0",
      total_position_im: update.total_margin_used.toString(),
      total_position_mm: existing?.total_position_mm ?? "0",
      unrealised_pnl: existing?.unrealised_pnl ?? "0",
      unrealized_funding: existing?.unrealized_funding ?? "0",
      realised_pnl_cum: existing?.realised_pnl_cum ?? "0",
    };

    runInAction(() => {
      this.setAccountBalance(subAccountAddr, next);
    });
  };

  private getLiveBalanceForActiveAccount = (rawBalance: AccountBalance, userAddress: string): AccountBalance => {
    const toFiniteNumber = (value: unknown): number => {
      const parsed = Number(value ?? 0);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const marketByAddress = new Map(
      this.rootStore.marketStore.allMarkets.map((market) => [Address.from(market.addr).toString(), market]),
    );

    const userPositions = this.rootStore.myOrdersStore.openPosition.filter(
      (position) =>
        Address.from(position.sub_account_address).toString() === userAddress && Number(position.size || 0) !== 0,
    );

    let liveUnrealizedPnl = 0;
    let liveTotalPositionMM = 0;

    for (const position of userPositions) {
      const market = marketByAddress.get(Address.from(position.market_addr).toString());
      if (!market) continue;

      const sizeAbs = Math.abs(toFiniteNumber(position.size));
      const signedSize = position.side === "sell" ? -sizeAbs : sizeAbs;
      const entryPrice = toFiniteNumber(position.entry_price ?? position.price ?? 0);
      const markPrice = toFiniteNumber(market.mark_price ?? 0);
      const maintenanceMarginRatio = toFiniteNumber(market.maintenance_margin_ratio ?? 0);

      if (Number.isFinite(signedSize) && Number.isFinite(entryPrice) && Number.isFinite(markPrice)) {
        liveUnrealizedPnl += signedSize * (markPrice - entryPrice);
      }

      if (Number.isFinite(sizeAbs) && Number.isFinite(markPrice) && Number.isFinite(maintenanceMarginRatio)) {
        liveTotalPositionMM += sizeAbs * markPrice * maintenanceMarginRatio;
      }
    }

    const baseEquity = toFiniteNumber(rawBalance.equity);
    const baseUnrealizedPnl = toFiniteNumber(rawBalance.unrealised_pnl);
    const baseMarginBalance =
      rawBalance.margin_balance !== undefined
        ? toFiniteNumber(rawBalance.margin_balance)
        : baseEquity - baseUnrealizedPnl;
    const unrealizedDelta = liveUnrealizedPnl - baseUnrealizedPnl;
    const liveMarginBalance = baseMarginBalance + unrealizedDelta;
    const liveEquity = baseEquity + unrealizedDelta;

    const totalPositionIM = toFiniteNumber(rawBalance.total_position_im);
    const totalOrderIM = toFiniteNumber(rawBalance.total_order_im);
    const liveAvailableBalance = liveEquity - totalPositionIM - totalOrderIM;

    return {
      ...rawBalance,
      equity: liveEquity.toString(),
      margin_balance: liveMarginBalance.toString(),
      available_balance: liveAvailableBalance.toString(),
      total_position_mm: liveTotalPositionMM.toString(),
      unrealised_pnl: liveUnrealizedPnl.toString(),
    };
  };

  getTestTokens = async (): Promise<boolean> => {
    const { accountStore, toastStore, configStore } = this.rootStore;
    if (!accountStore.wallet?.account) return false;

    const address = accountStore.wallet.account.address.toString();
    const quoteAssetMetadata = configStore.quoteAssetMetadata;
    this.setTestTokensRequested(true);

    const success = await balanceService.requestTestTokens(
      address,
      quoteAssetMetadata,
      () =>
        toastStore.show({
          type: "info",
          title: "Pending",
          content: "Tokens have been sent to your account, please wait 2-3 minutes.",
          duration: 9999999999999,
        }),
      (toastId) => {
        toastStore.close(toastId);
        toastStore.show({
          type: "success",
          title: "Test tokens requested",
          content: "Test tokens requested successfully! Check your wallet.",
        });
        this.onChainBalanceUpdater.run(true);
      },
      (message) => {
        toastStore.show({
          type: "error",
          title: "Error Requesting Test Tokens",
          content: message,
        });
      },
    );

    this.setTestTokensRequested(false);
    return success;
  };

  private setupReactions = () => {
    const { accountStore, configStore } = this.rootStore;

    reaction(
      () => accountStore.wallet?.account,
      (account) => {
        const address = account?.address.toString();
        if (isValidAddress(address)) {
          this.fetchOnChainBalance(address);
          this.onChainBalanceUpdater.run();
        } else {
          this.onChainBalanceUpdater.stop();
        }
      },
    );

    reaction(
      () => [accountStore.subAccountAddress],
      ([address]) => {
        if (isValidAddress(address)) {
          this.fetchOnChainBalance(address);
        }
      },
      { fireImmediately: true },
    );

    reaction(
      () => [accountStore.rootAccountToken, accountStore.isFullyConnected] as const,
      ([rootToken, isFullyConnected]) => {
        if (rootToken && isFullyConnected) {
          this.fetchAccountBalances();
        }
      },
      { fireImmediately: true },
    );

    // Re-fetch on-chain balance when quoteAssetMetadata becomes available from config
    reaction(
      () => configStore.quoteAssetMetadata,
      (quoteAssetMetadata) => {
        if (quoteAssetMetadata) {
          const address = accountStore.wallet?.account?.address.toString();
          if (isValidAddress(address)) {
            this.fetchOnChainBalance(address);
          }
        }
      },
    );
  };

  private mapTokenAmounts = (balances?: Map<string, string>, shouldCheckWithOpenOrders = false): Token[] => {
    const { marketStore, myOrdersStore, configStore } = this.rootStore;

    // Always ensure quote asset is in the tokens list for deposit/withdraw operations
    const tokens = [...marketStore.tokens];
    const quoteAssetAddress = configStore.quoteAssetMetadata;

    if (quoteAssetAddress) {
      const quoteAssetNormalized = Address.from(quoteAssetAddress).toString();
      const hasQuoteAsset = tokens.some((t) => Address.from(t.address).toString() === quoteAssetNormalized);

      if (!hasQuoteAsset) {
        const quoteAssetMeta = TOKEN_METADATA[quoteAssetAddress] || TOKEN_METADATA[quoteAssetNormalized];
        if (quoteAssetMeta) {
          tokens.push({
            ...quoteAssetMeta,
            address: quoteAssetAddress,
            decimals: 6,
          });
        }
      }
    }

    return tokens.map((token) => {
      const address = Address.from(token.address).toString();
      const amount = balances?.get(address) ?? "0";
      let amountBN = new BN(amount);

      if (shouldCheckWithOpenOrders) {
        const total = myOrdersStore.openBalancesByToken.get(token.address) ?? BN.ZERO;
        amountBN = BN.max(BN.ZERO, amountBN.minus(BN.parseUnits(total, token.decimals)));
      }

      const isQuoteAsset =
        quoteAssetAddress &&
        (token.address === quoteAssetAddress || address === Address.from(quoteAssetAddress).toString());
      const decimals = Number(token.decimals) || 0;
      const formattedAmount = BN.formatUnits(amountBN, decimals);

      return {
        ...token,
        amount: amountBN,
        amountFormatted: formatAmount(amountBN, decimals),
        amountUsd: isQuoteAsset ? formattedAmount.toString() : "0",
      } satisfies Token;
    });
  };

  reset = () => {
    this.onChainBalanceUpdater.stop();
    this.onChainBalances.clear();
    this.accountBalanceByUser.clear();
    this.loading = false;
    this.isTestTokensRequested = false;
  };
}
